import { findIndex } from "@fluentui/react/lib/Utilities";

import {
  AppSpinButtonUnit,
  AppSpinButtonUnsetValue,
  AppSpinButtonValue,
  NumberFormatInfo,
} from "./AppSpinButton.Props";

/* A placeholder where the number should be placed when appending value and units. For example '{0}"' or '{0} cm' */
const VALUE_PLACEHOLDER = "{0}";

/**
 * Parses a string containing a number with units. Returns the parsed numeric value with units as AppSpinButtonUnit
 * Example: '3"' will return 3 and a AppSpinButtonUnit with unit type 'Inch'
 */
export function parseSpinButtonValue(
  valueWithUnits: string,
  validUnits: AppSpinButtonUnit[],
  numberFormatInfo: NumberFormatInfo,
  currentUnitType: string
): { value: number; unit: AppSpinButtonUnit | undefined } {
  let unit = undefined;
  /* eslint-disable-next-line prefer-const */
  let { value, unitsPlaceholder } = extractNumericValue(
    valueWithUnits,
    numberFormatInfo
  );

  if (value === undefined || isNaN(value) || unitsPlaceholder === undefined) {
    value = Number.NaN;
    return { value, unit };
  }

  unit = getSpinButtonUnit(unitsPlaceholder, validUnits, currentUnitType);

  return { value, unit };
}

/** Creates a string representation of the passed value and units to be displayed in the Spin Button. It handles the reserved value case */
export function formatSpinButtonValue(
  value: AppSpinButtonValue,
  unitType: string,
  validUnits: AppSpinButtonUnit[],
  numberFormat: NumberFormatInfo,
  reservedValue?: string
): string {
  if (value === AppSpinButtonUnsetValue) {
    return value;
  }
  if (reservedValue !== undefined) {
    return reservedValue;
  }

  const numberValueString = generateNumberValueAsString(value, numberFormat);
  const unitsPlaceholder = getUnitPlaceholder(unitType, validUnits);
  if (unitsPlaceholder === undefined) {
    return numberValueString;
  }

  return unitsPlaceholder.replace(VALUE_PLACEHOLDER, numberValueString);
}

/** Returns the new value as a number after spinning up or down */
export function getNewSpinValue(
  value: number,
  unit: AppSpinButtonUnit,
  spinningUp: boolean,
  numberFormat: NumberFormatInfo,
  customNormalizeValue?: (value: number, unit: AppSpinButtonUnit) => number
): number {
  // Create an inflation multiplier according to the number of decimal digits.
  const inflationMult = Math.pow(10.0, numberFormat.numberDecimalDigits);

  // Create inflated integers.
  const inflatedValue = Math.round(value * inflationMult);
  const inflatedStep = Math.round(unit.step * inflationMult);

  // Obtain the inflated factor depending if spinning up or down
  let inflatedFactor = inflatedValue / inflatedStep;
  inflatedFactor = spinningUp
    ? Math.floor(inflatedFactor)
    : Math.ceil(inflatedFactor);

  // Multiply the factor and the step. Then divide by the inflation to get the starting value.
  const startingValue = (inflatedFactor * inflatedStep) / inflationMult;
  const newValue = spinningUp
    ? startingValue + unit.step
    : startingValue - unit.step;

  return customNormalizeValue
    ? customNormalizeValue(newValue, unit)
    : normalizeValue(newValue, unit);
}

/** Normalizes the value to be between the boundaries of min and max values */
export function normalizeValue(value: number, unit: AppSpinButtonUnit): number {
  if (value < unit.minValue) {
    return unit.minValue;
  }

  if (value > unit.maxValue) {
    return unit.maxValue;
  }

  return value;
}

/** Checks the value against the unit range */
export function isValueWithinUnitRange(
  value: number,
  unit: AppSpinButtonUnit
): boolean {
  return !(
    value === undefined ||
    isNaN(value) ||
    value < unit.minValue ||
    value > unit.maxValue
  );
}

/** Returns the string representation of a number depending on the format info */
export function generateNumberValueAsString(
  value: number,
  numberFormat: NumberFormatInfo
): string {
  let wholeValue = "";
  let decimalValue = "";

  // BUG 2050509 - We're being passed in undefined values which results in us creating NaNNaN in our spin button
  // in Dogfood when the ribbon is disabled. This is a temporary fix until we can find out the root cause
  if (value === undefined || isNaN(value) || numberFormat === undefined) {
    return "0";
  }

  // First, round the value depending on the number of decimal digits.
  const digitsMultiplier = Math.pow(10, numberFormat.numberDecimalDigits);
  const roundedAbsoluteNumber = Math.abs(
    Math.round(value * digitsMultiplier) / digitsMultiplier
  );

  // Second, get the integer and decimal parts of the number
  const wholeNumber = Math.floor(roundedAbsoluteNumber);
  const decimalNumber =
    (roundedAbsoluteNumber * digitsMultiplier -
      wholeNumber * digitsMultiplier) /
    digitsMultiplier;

  if (wholeNumber === 0) {
    wholeValue = "0";
  } else {
    // Use the groups to determine where to put the separators
    const numGroups = numberFormat.numberGroupSizes.length;
    let group = 0;
    let wholeDigits = wholeNumber;

    while (wholeDigits > 0) {
      const groupSize = numberFormat.numberGroupSizes[group];

      // If group size is 0, concatenate the digits with wholeValue
      if (groupSize === 0) {
        wholeValue = `${wholeDigits.toString()}${wholeValue}`;
        wholeDigits = 0;
      } else {
        // Format based on the group size
        for (let i = 0; i < groupSize && wholeDigits > 0; i += 1) {
          const nextWholeDigit = wholeDigits % 10;
          wholeValue = `${nextWholeDigit.toString()}${wholeValue}`;
          wholeDigits = Math.floor(wholeDigits / 10);
        }

        // Add the group separator to the current group
        if (wholeDigits > 0) {
          wholeValue = `${numberFormat.numberGroupSeparator}${wholeValue}`;
        }
      }

      // Increase the group index.
      // If we are at the end of the number of groups, then use the last group.
      if (group < numGroups - 1) {
        group += 1;
      }
    }
  }

  // Convert the decimal digits to a string
  let decimalDigitsValue = "";
  let decimalDigitsRemaining =
    Math.round(decimalNumber * digitsMultiplier) / digitsMultiplier;
  for (let i = 0; i < numberFormat.numberDecimalDigits; i += 1) {
    const currentDecimalDigit = Math.floor(decimalDigitsRemaining * 10);
    decimalDigitsValue = `${decimalDigitsValue}${currentDecimalDigit.toString()}`;

    const remainingDigitsMultiplier = Math.pow(
      10,
      numberFormat.numberDecimalDigits - i - 1
    );
    decimalDigitsRemaining =
      Math.round(
        decimalDigitsRemaining * remainingDigitsMultiplier * 10 -
          currentDecimalDigit * remainingDigitsMultiplier
      ) / remainingDigitsMultiplier;
  }

  // Remove extra digits from the decimal digits.
  let trimLocation = decimalDigitsValue.length;
  while (
    trimLocation > 0 &&
    decimalDigitsValue.charAt(trimLocation - 1) === "0"
  ) {
    trimLocation -= 1;
  }

  decimalDigitsValue = decimalDigitsValue.substring(
    0,
    Math.max(0, trimLocation)
  );

  // Create the decimal value using the decimal separator
  if (decimalDigitsValue !== "") {
    decimalValue = `${decimalValue}${numberFormat.numberDecimalSeparator}${decimalDigitsValue}`;
  }

  // Append whole and decimal values
  let valueString = `${wholeValue}${decimalValue}`;

  // Add negative sign if negative number
  if (value < 0) {
    switch (numberFormat.numberNegativePattern) {
      case 0:
        valueString = `(${valueString})`;
        break;

      case 1:
        valueString = `${numberFormat.negativeSign}${valueString}`;
        break;

      case 2:
        valueString = `${numberFormat.negativeSign} ${valueString}`;
        break;

      case 3:
        valueString = `${valueString}${numberFormat.negativeSign}`;
        break;

      case 4:
        valueString = `${valueString} ${numberFormat.negativeSign}`;
        break;
    }
  }

  return valueString;
}

/** Returns the number from the passed string using the format provided by NumberFormatInfo.
 * It also returns the string when changing the extracted number to a placeholder
 */
export function extractNumericValue(
  valueWithUnits: string,
  numberFormat: NumberFormatInfo
): { value: number | undefined; unitsPlaceholder: string | undefined } {
  let value = undefined;
  let unitsPlaceholder = undefined;
  // Add escape character to ensure that . and * will be parsed as the value and not a regex
  const sanitizeRegExp = new RegExp(
    "\\" + numberFormat.numberGroupSeparator,
    "g" /*Find all*/
  );
  const sanitizedValue = valueWithUnits.replace(sanitizeRegExp, "");
  const absoluteNumberPattern = getAbsoluteNumberPattern(numberFormat);
  const absoluteRegExp = new RegExp(absoluteNumberPattern, "g" /*Find all*/);
  const absoluteMatches = sanitizedValue.match(absoluteRegExp);

  if (absoluteMatches === null || absoluteMatches.length !== 1) {
    return { value, unitsPlaceholder };
  }

  // Get negative numbers from the string
  const negativeNumberPattern = getNegativeNumberPattern(
    numberFormat,
    absoluteNumberPattern
  );
  const negativeMatches = sanitizedValue.match(negativeNumberPattern);

  unitsPlaceholder =
    negativeMatches !== null
      ? sanitizedValue.replace(negativeMatches[0], VALUE_PLACEHOLDER)
      : sanitizedValue.replace(absoluteMatches[0], VALUE_PLACEHOLDER);
  unitsPlaceholder = removeAllWhitespaces(unitsPlaceholder);
  unitsPlaceholder = unitsPlaceholder.toLowerCase();

  // Format the number for JavaScript
  let numberString = `${negativeMatches !== null ? "-" : ""}${
    absoluteMatches[0]
  }`;
  numberString = numberString.replace(numberFormat.numberDecimalSeparator, ".");
  value = parseFloat(numberString);

  return { value, unitsPlaceholder };
}

/**Generates a string that represents a RegExp to match absolute numbers in a string. */
export function getAbsoluteNumberPattern(
  numberFormat: NumberFormatInfo
): string {
  const decimalSeparatorPattern = `[${numberFormat.numberDecimalSeparator}]`;
  const numberPattern = "[0-9]";
  const absoluteNumberPattern = `${numberPattern}*${decimalSeparatorPattern}?${numberPattern}+`;

  return absoluteNumberPattern;
}

/** Generates a string that represents a RegExp to match negative numbers in a string */
export function getNegativeNumberPattern(
  numberFormat: NumberFormatInfo,
  absoluteNumberPattern: string
): string {
  let negativeNumberPattern = "";
  const negativeSign = numberFormat.negativeSign;

  switch (numberFormat.numberNegativePattern) {
    case 0:
      negativeNumberPattern = `[(][ ]?${absoluteNumberPattern}[ ]?[)]`;
      break;

    case 1:
    case 2:
      negativeNumberPattern = `${negativeSign}[ ]?${absoluteNumberPattern}`;
      break;

    case 3:
    case 4:
      negativeNumberPattern = `${absoluteNumberPattern}[ ]?${negativeSign}`;
      break;
  }

  return negativeNumberPattern;
}

/** Returns the unit that matches the unitsPlaceholder*/
export function getSpinButtonUnit(
  unitsPlaceholder: string,
  validUnits: AppSpinButtonUnit[],
  curUnitType: string
): AppSpinButtonUnit | undefined {
  // If unitsPlaceholder only has the placeholder for the number, then return the current unit type.
  // This is in the case when user inserts only a number without units.
  if (unitsPlaceholder === VALUE_PLACEHOLDER) {
    return getUnitFromUnitType(curUnitType, validUnits);
  }

  for (let i = 0; i < validUnits.length; i += 1) {
    let unitString = getUnitPlaceholder(validUnits[i].unitType, validUnits);
    if (unitString !== undefined) {
      unitString = removeAllWhitespaces(unitString);

      if (unitString === unitsPlaceholder) {
        return validUnits[i];
      }
    }
  }

  return undefined;
}

/** Returns the units with a placeholder as a string given the unitType and a set of validUnits */
export function getUnitPlaceholder(
  unitType: string,
  validUnits: AppSpinButtonUnit[]
): string | undefined {
  const unit = getUnitFromUnitType(unitType, validUnits);
  if (unit !== undefined) {
    return unit.unitPlaceholder;
  }

  return undefined;
}

/** Returns the units as AppSpinButtonUnit given unitType and a set of validUnits */
export function getUnitFromUnitType(
  unitType: string,
  validUnits: AppSpinButtonUnit[]
): AppSpinButtonUnit | undefined {
  const index = findIndex(validUnits, (unit) => unit.unitType === unitType);
  return index >= 0 ? validUnits[index] : undefined;
}

/** Removes all the whitespaces from the string */
export function removeAllWhitespaces(str: string): string {
  const regex = /\s/g;
  return str.replace(regex, "");
}

/** Returns a reserved value from the array if it matches newValue. Otherwise, return undefined */
export function tryGetReservedValue(
  newValue: string,
  validReservedValues: string[]
): string | undefined {
  const index = tryGetReservedValueIndex(newValue, validReservedValues);
  return index >= 0 ? validReservedValues[index] : undefined;
}

/** Returns a reserved value index from the array if it matches newValue. Otherwise, return -1 */
export function tryGetReservedValueIndex(
  newValue: string,
  validReservedValues: string[]
): number {
  return findIndex(
    validReservedValues,
    (reservedValue) =>
      reservedValue.toLowerCase() === newValue.trim().toLowerCase()
  );
}
